import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  LocationCountry, 
  CountryCreateDto, 
  LocationRegion, 
  RegionCreateDto, 
  LocationCity, 
  CityCreateDto,
  CountryBulkCreateDto,
  CountryResponse,
  RegionResponse,
  CityResponse
} from "../../modules/shared/model";
import { 
  createCountry as createCountryAction, 
  updateCountry as updateCountryAction, 
  getCountryById as getCountryByIdAction, 
  getCountries as getCountriesAction, 
  deleteCountry as deleteCountryAction,
  bulkCreateCountries as bulkCreateCountriesAction,
  bulkImportCsv as bulkImportCsvAction,
  BulkImportCsvResult,
  createRegion as createRegionAction,
  updateRegion as updateRegionAction,
  getRegionById as getRegionByIdAction,
  getRegions as getRegionsAction,
  deleteRegion as deleteRegionAction,
  createCity as createCityAction,
  updateCity as updateCityAction,
  getCityById as getCityByIdAction,
  deleteCity as deleteCityAction
} from "../Actions/LocationActions";

// Countries Async Thunks
export const createCountry = createAsyncThunk("locations/createCountry", async (data: CountryCreateDto, { rejectWithValue }) => {
  try { return await createCountryAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create country"); }
});

export const updateCountry = createAsyncThunk("locations/updateCountry", async ({ id, data }: { id: string; data: Partial<CountryCreateDto> }, { rejectWithValue }) => {
  try { return await updateCountryAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update country"); }
});

export const fetchCountry = createAsyncThunk("locations/getCountryById", async (id: string, { rejectWithValue }) => {
  try { return await getCountryByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch country"); }
});

export const fetchCountries = createAsyncThunk("locations/getCountries", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { return await getCountriesAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch countries"); }
});

export const fetchCountriesPage = createAsyncThunk("locations/getCountriesPage", async ({ page, search }: { page: number; search?: string }, { rejectWithValue }) => {
  try { 
    const query: Record<string, string | number | boolean> = { page, limit: 10 };
    if (search) query.name = search;
    return await getCountriesAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch countries page"); 
  }
});

export const deleteCountry = createAsyncThunk("locations/deleteCountry", async (id: string, { rejectWithValue }) => {
  try { await deleteCountryAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete country"); }
});

export const bulkCreateCountries = createAsyncThunk("locations/bulkCreateCountries", async (data: CountryBulkCreateDto, { rejectWithValue }) => {
  try { return await bulkCreateCountriesAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to bulk create countries"); }
});

export const bulkImportCsv = createAsyncThunk("locations/bulkImportCsv", async (file: File, { rejectWithValue }) => {
  try { return await bulkImportCsvAction(file); } catch (e: any) { return rejectWithValue(e?.message || "Failed to import CSV file"); }
});

// Regions Async Thunks
export const createRegion = createAsyncThunk("locations/createRegion", async (data: RegionCreateDto, { rejectWithValue }) => {
  try { return await createRegionAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create region"); }
});

export const updateRegion = createAsyncThunk("locations/updateRegion", async ({ id, data }: { id: string; data: Partial<RegionCreateDto> }, { rejectWithValue }) => {
  try { return await updateRegionAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update region"); }
});

export const fetchRegion = createAsyncThunk("locations/getRegionById", async (id: string, { rejectWithValue }) => {
  try { return await getRegionByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch region"); }
});

export const fetchRegions = createAsyncThunk("locations/getRegions", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getRegionsAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch regions"); 
  }
});

export const deleteRegion = createAsyncThunk("locations/deleteRegion", async (id: string, { rejectWithValue }) => {
  try { await deleteRegionAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete region"); }
});

export const fetchRegionsByCountry = createAsyncThunk("locations/getRegionsByCountry", async (countryId: string, { getState, rejectWithValue }) => {
  try { 
    const state = getState() as { locations: any };
    const country = state.locations.countries.find((c: LocationCountry) => c.id === countryId);
    if (!country) {
      return rejectWithValue("Country not found");
    }
    return country.regions || [];
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch regions by country"); 
  }
});

// Cities Async Thunks
export const createCity = createAsyncThunk("locations/createCity", async (data: CityCreateDto, { rejectWithValue }) => {
  try { return await createCityAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create city"); }
});

export const updateCity = createAsyncThunk("locations/updateCity", async ({ id, data }: { id: string; data: Partial<CityCreateDto> }, { rejectWithValue }) => {
  try { return await updateCityAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update city"); }
});

export const fetchCity = createAsyncThunk("locations/getCityById", async (id: string, { rejectWithValue }) => {
  try { return await getCityByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch city"); }
});

export const fetchCities = createAsyncThunk("locations/getCities", async (query: Record<string, string | number | boolean> | undefined, { getState, rejectWithValue }) => {
  try { 
    const state = getState() as { locations: any };
    // Get all cities from all countries
    const allCities: LocationCity[] = [];
    state.locations.countries.forEach((country: LocationCountry) => {
      if (country.cities) {
        allCities.push(...country.cities);
      }
      // Also get cities from regions
      if (country.regions) {
        country.regions.forEach((region: LocationRegion) => {
          if (region.cities) {
            allCities.push(...region.cities);
          }
        });
      }
    });
    
    // Create a mock response structure to match the expected format
    const response = {
      data: allCities,
      hasNextPage: false,
      hasPreviousPage: false,
      page: 1,
      limit: allCities.length,
      totalCount: allCities.length
    };
    
    return response;
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch cities"); 
  }
});

export const deleteCity = createAsyncThunk("locations/deleteCity", async (id: string, { rejectWithValue }) => {
  try { await deleteCityAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete city"); }
});

export const fetchCitiesByRegion = createAsyncThunk("locations/getCitiesByRegion", async (regionId: string, { getState, rejectWithValue }) => {
  try { 
    const state = getState() as { locations: any };
    const region = state.locations.regions.find((r: LocationRegion) => r.id === regionId);
    if (!region) {
      return rejectWithValue("Region not found");
    }
    return region.cities || [];
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch cities by region"); 
  }
});

export const fetchCitiesByCountry = createAsyncThunk("locations/getCitiesByCountry", async (countryId: string, { getState, rejectWithValue }) => {
  try { 
    const state = getState() as { locations: any };
    const country = state.locations.countries.find((c: LocationCountry) => c.id === countryId);
    if (!country) {
      return rejectWithValue("Country not found");
    }
    return country.cities || [];
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch cities by country"); 
  }
});

interface LocationState {
  // Countries
  countries: LocationCountry[];
  currentCountry: LocationCountry | null;
  countriesLoading: boolean;
  countriesError: string | null;
  countriesTotalCount?: number;
  countriesCurrentPage: number;
  countriesHasNextPage: boolean;
  countriesHasPreviousPage: boolean;
  countriesSearchQuery: string;
  // Regions
  regions: LocationRegion[];
  currentRegion: LocationRegion | null;
  regionsLoading: boolean;
  regionsError: string | null;
  regionsTotalCount?: number;
  regionsSearchQuery: string;
  // Cities
  cities: LocationCity[];
  currentCity: LocationCity | null;
  citiesLoading: boolean;
  citiesError: string | null;
  citiesTotalCount?: number;
  citiesSearchQuery: string;
}

const initialState: LocationState = {
  // Countries
  countries: [],
  currentCountry: null,
  countriesLoading: false,
  countriesError: null,
  countriesTotalCount: 0,
  countriesCurrentPage: 1,
  countriesHasNextPage: false,
  countriesHasPreviousPage: false,
  countriesSearchQuery: "",
  // Regions
  regions: [],
  currentRegion: null,
  regionsLoading: false,
  regionsError: null,
  regionsTotalCount: 0,
  regionsSearchQuery: "",
  // Cities
  cities: [],
  currentCity: null,
  citiesLoading: false,
  citiesError: null,
  citiesTotalCount: 0,
  citiesSearchQuery: "",
};

const locationSlice = createSlice({
  name: "locations",
  initialState,
  reducers: {
    clearCountriesError(state) { state.countriesError = null; },
    clearCurrentCountry(state) { state.currentCountry = null; },
    clearRegionsError(state) { state.regionsError = null; },
    clearCurrentRegion(state) { state.currentRegion = null; },
    clearCitiesError(state) { state.citiesError = null; },
    clearCurrentCity(state) { state.currentCity = null; },
    setCountriesSearchQuery(state, action: PayloadAction<string>) { 
      state.countriesSearchQuery = action.payload; 
    },
    setRegionsSearchQuery(state, action: PayloadAction<string>) { 
      state.regionsSearchQuery = action.payload; 
    },
    setCitiesSearchQuery(state, action: PayloadAction<string>) { 
      state.citiesSearchQuery = action.payload; 
    },
    clearRegions(state) {
      state.regions = [];
      state.currentRegion = null;
      state.regionsTotalCount = 0;
    },
    clearCities(state) {
      state.cities = [];
      state.currentCity = null;
      state.citiesTotalCount = 0;
    }
  },
  extraReducers: (builder) => {
    // Countries Extra Reducers
    builder
      .addCase(createCountry.pending, (s) => { s.countriesLoading = true; s.countriesError = null; })
      .addCase(createCountry.fulfilled, (s, a: PayloadAction<LocationCountry>) => { s.countriesLoading = false; s.countries.unshift(a.payload); s.countriesTotalCount = (s.countriesTotalCount || 0) + 1; })
      .addCase(createCountry.rejected, (s, a: any) => { s.countriesLoading = false; s.countriesError = a.payload as string; });

    builder
      .addCase(updateCountry.pending, (s) => { s.countriesLoading = true; s.countriesError = null; })
      .addCase(updateCountry.fulfilled, (s, a: PayloadAction<LocationCountry>) => {
        s.countriesLoading = false;
        const i = s.countries.findIndex(x => x.id === a.payload.id);
        if (i !== -1) s.countries[i] = a.payload;
        if (s.currentCountry?.id === a.payload.id) s.currentCountry = a.payload;
      })
      .addCase(updateCountry.rejected, (s, a: any) => { s.countriesLoading = false; s.countriesError = a.payload as string; });

    builder
      .addCase(fetchCountry.pending, (s) => { s.countriesLoading = true; s.countriesError = null; })
      .addCase(fetchCountry.fulfilled, (s, a: PayloadAction<LocationCountry>) => { s.countriesLoading = false; s.currentCountry = a.payload; })
      .addCase(fetchCountry.rejected, (s, a: any) => { s.countriesLoading = false; s.countriesError = a.payload as string; });

    builder
      .addCase(fetchCountries.pending, (s) => { s.countriesLoading = true; s.countriesError = null; })
      .addCase(fetchCountries.fulfilled, (s, a: PayloadAction<CountryResponse>) => { 
        s.countriesLoading = false; 
        s.countries = a.payload.data || []; 
        s.countriesTotalCount = a.payload.totalCount || 0; 
        s.countriesCurrentPage = a.payload.page || 1;
        s.countriesHasNextPage = a.payload.hasNextPage || false;
        s.countriesHasPreviousPage = a.payload.hasPreviousPage || false;
      })
      .addCase(fetchCountries.rejected, (s, a: any) => { s.countriesLoading = false; s.countriesError = a.payload as string; });

    builder
      .addCase(fetchCountriesPage.pending, (s) => { s.countriesLoading = true; s.countriesError = null; })
      .addCase(fetchCountriesPage.fulfilled, (s, a: PayloadAction<CountryResponse>) => { 
        s.countriesLoading = false; 
        s.countries = a.payload.data || []; 
        s.countriesTotalCount = a.payload.totalCount || 0; 
        s.countriesCurrentPage = a.payload.page || 1;
        s.countriesHasNextPage = a.payload.hasNextPage || false;
        s.countriesHasPreviousPage = a.payload.hasPreviousPage || false;
      })
      .addCase(fetchCountriesPage.rejected, (s, a: any) => { s.countriesLoading = false; s.countriesError = a.payload as string; });

    builder
      .addCase(deleteCountry.pending, (s) => { s.countriesLoading = true; s.countriesError = null; })
      .addCase(deleteCountry.fulfilled, (s, a: PayloadAction<string>) => { s.countriesLoading = false; s.countries = s.countries.filter(x => x.id !== a.payload); if (s.currentCountry?.id === a.payload) s.currentCountry = null; s.countriesTotalCount = Math.max(0, (s.countriesTotalCount || 0) - 1); })
      .addCase(deleteCountry.rejected, (s, a: any) => { s.countriesLoading = false; s.countriesError = a.payload as string; });

    builder
      .addCase(bulkCreateCountries.pending, (s) => { s.countriesLoading = true; s.countriesError = null; })
      .addCase(bulkCreateCountries.fulfilled, (s, a: PayloadAction<LocationCountry[]>) => { s.countriesLoading = false; s.countries.unshift(...a.payload); s.countriesTotalCount = (s.countriesTotalCount || 0) + a.payload.length; })
      .addCase(bulkCreateCountries.rejected, (s, a: any) => { s.countriesLoading = false; s.countriesError = a.payload as string; });

    builder
      .addCase(bulkImportCsv.pending, (s) => { s.countriesLoading = true; s.countriesError = null; })
      .addCase(bulkImportCsv.fulfilled, (s, a: PayloadAction<BulkImportCsvResult>) => { 
        s.countriesLoading = false;
        // The import result doesn't return the actual data, so we'll need to refresh the list
      })
      .addCase(bulkImportCsv.rejected, (s, a: any) => { s.countriesLoading = false; s.countriesError = a.payload as string; });

    // Regions Extra Reducers
    builder
      .addCase(createRegion.pending, (s) => { s.regionsLoading = true; s.regionsError = null; })
      .addCase(createRegion.fulfilled, (s, a: PayloadAction<LocationRegion>) => { s.regionsLoading = false; s.regions.unshift(a.payload); s.regionsTotalCount = (s.regionsTotalCount || 0) + 1; })
      .addCase(createRegion.rejected, (s, a: any) => { s.regionsLoading = false; s.regionsError = a.payload as string; });

    builder
      .addCase(updateRegion.pending, (s) => { s.regionsLoading = true; s.regionsError = null; })
      .addCase(updateRegion.fulfilled, (s, a: PayloadAction<LocationRegion>) => {
        s.regionsLoading = false;
        const i = s.regions.findIndex(x => x.id === a.payload.id);
        if (i !== -1) s.regions[i] = a.payload;
        if (s.currentRegion?.id === a.payload.id) s.currentRegion = a.payload;
      })
      .addCase(updateRegion.rejected, (s, a: any) => { s.regionsLoading = false; s.regionsError = a.payload as string; });

    builder
      .addCase(fetchRegion.pending, (s) => { s.regionsLoading = true; s.regionsError = null; })
      .addCase(fetchRegion.fulfilled, (s, a: PayloadAction<LocationRegion>) => { s.regionsLoading = false; s.currentRegion = a.payload; })
      .addCase(fetchRegion.rejected, (s, a: any) => { s.regionsLoading = false; s.regionsError = a.payload as string; });

    builder
      .addCase(fetchRegions.pending, (s) => { s.regionsLoading = true; s.regionsError = null; })
      .addCase(fetchRegions.fulfilled, (s, a: PayloadAction<RegionResponse>) => { s.regionsLoading = false; s.regions = a.payload.data || []; s.regionsTotalCount = a.payload.totalCount || 0; })
      .addCase(fetchRegions.rejected, (s, a: any) => { s.regionsLoading = false; s.regionsError = a.payload as string; });

    builder
      .addCase(deleteRegion.pending, (s) => { s.regionsLoading = true; s.regionsError = null; })
      .addCase(deleteRegion.fulfilled, (s, a: PayloadAction<string>) => { s.regionsLoading = false; s.regions = s.regions.filter(x => x.id !== a.payload); if (s.currentRegion?.id === a.payload) s.currentRegion = null; s.regionsTotalCount = Math.max(0, (s.regionsTotalCount || 0) - 1); })
      .addCase(deleteRegion.rejected, (s, a: any) => { s.regionsLoading = false; s.regionsError = a.payload as string; });

    builder
      .addCase(fetchRegionsByCountry.pending, (s) => { s.regionsLoading = true; s.regionsError = null; })
      .addCase(fetchRegionsByCountry.fulfilled, (s, a: PayloadAction<LocationRegion[]>) => { s.regionsLoading = false; s.regions = a.payload || []; })
      .addCase(fetchRegionsByCountry.rejected, (s, a: any) => { s.regionsLoading = false; s.regionsError = a.payload as string; });

    // Cities Extra Reducers
    builder
      .addCase(createCity.pending, (s) => { s.citiesLoading = true; s.citiesError = null; })
      .addCase(createCity.fulfilled, (s, a: PayloadAction<LocationCity>) => { s.citiesLoading = false; s.cities.unshift(a.payload); s.citiesTotalCount = (s.citiesTotalCount || 0) + 1; })
      .addCase(createCity.rejected, (s, a: any) => { s.citiesLoading = false; s.citiesError = a.payload as string; });

    builder
      .addCase(updateCity.pending, (s) => { s.citiesLoading = true; s.citiesError = null; })
      .addCase(updateCity.fulfilled, (s, a: PayloadAction<LocationCity>) => {
        s.citiesLoading = false;
        const i = s.cities.findIndex(x => x.id === a.payload.id);
        if (i !== -1) s.cities[i] = a.payload;
        if (s.currentCity?.id === a.payload.id) s.currentCity = a.payload;
      })
      .addCase(updateCity.rejected, (s, a: any) => { s.citiesLoading = false; s.citiesError = a.payload as string; });

    builder
      .addCase(fetchCity.pending, (s) => { s.citiesLoading = true; s.citiesError = null; })
      .addCase(fetchCity.fulfilled, (s, a: PayloadAction<LocationCity>) => { s.citiesLoading = false; s.currentCity = a.payload; })
      .addCase(fetchCity.rejected, (s, a: any) => { s.citiesLoading = false; s.citiesError = a.payload as string; });

    builder
      .addCase(fetchCities.pending, (s) => { s.citiesLoading = true; s.citiesError = null; })
      .addCase(fetchCities.fulfilled, (s, a: PayloadAction<CityResponse>) => { s.citiesLoading = false; s.cities = a.payload.data || []; s.citiesTotalCount = a.payload.totalCount || 0; })
      .addCase(fetchCities.rejected, (s, a: any) => { s.citiesLoading = false; s.citiesError = a.payload as string; });

    builder
      .addCase(deleteCity.pending, (s) => { s.citiesLoading = true; s.citiesError = null; })
      .addCase(deleteCity.fulfilled, (s, a: PayloadAction<string>) => { s.citiesLoading = false; s.cities = s.cities.filter(x => x.id !== a.payload); if (s.currentCity?.id === a.payload) s.currentCity = null; s.citiesTotalCount = Math.max(0, (s.citiesTotalCount || 0) - 1); })
      .addCase(deleteCity.rejected, (s, a: any) => { s.citiesLoading = false; s.citiesError = a.payload as string; });

    builder
      .addCase(fetchCitiesByRegion.pending, (s) => { s.citiesLoading = true; s.citiesError = null; })
      .addCase(fetchCitiesByRegion.fulfilled, (s, a: PayloadAction<LocationCity[]>) => { s.citiesLoading = false; s.cities = a.payload || []; })
      .addCase(fetchCitiesByRegion.rejected, (s, a: any) => { s.citiesLoading = false; s.citiesError = a.payload as string; });

    builder
      .addCase(fetchCitiesByCountry.pending, (s) => { s.citiesLoading = true; s.citiesError = null; })
      .addCase(fetchCitiesByCountry.fulfilled, (s, a: PayloadAction<LocationCity[]>) => { s.citiesLoading = false; s.cities = a.payload || []; })
      .addCase(fetchCitiesByCountry.rejected, (s, a: any) => { s.citiesLoading = false; s.citiesError = a.payload as string; });
  }
});

export const { 
  clearCountriesError, 
  clearCurrentCountry, 
  clearRegionsError,
  clearCurrentRegion,
  clearRegions,
  clearCitiesError,
  clearCurrentCity,
  clearCities,
  setCountriesSearchQuery,
  setRegionsSearchQuery,
  setCitiesSearchQuery
} = locationSlice.actions;
export default locationSlice.reducer;
